import { useCallback, useEffect, useMemo } from "react";

import { useNode } from "@craftjs/core";

import { useLoadFormMetadata } from "@/modules/cms/views/post/components/builder/hooks/useMetadata";
import useProp from "@/modules/cms/views/post/components/builder/hooks/useProp";
import { useFormStore, FormProvider as FormProviderStore } from "@/modules/offer/views/form/store";
import createContextFactory from "@/utils/factory";

import { useCraftProp } from "../../utils";

const { Provider, useContext } = createContextFactory(({ setNodeId }) => {
  const step = useProp()
  const form = useProp(null)
  const formId = useProp()

  useLoadFormMetadata(formId)

  return useMemo(() => ({
    step, form, setNodeId, formId,
  }), [step, form, setNodeId, formId])
})

export function FormProvider({ children, setNodeId }) {
  return <FormProviderStore>
    <Provider setNodeId={setNodeId}>
      {children}
    </Provider>
  </FormProviderStore>
}

export function useFormLoader({ formId, step }) {
  const { collection: { useLoad: useLoadCollection } } = useFormStore()
  const { step: { setValue: setStep }, formId: { setValue: setFormId } } = useContext()

  useLoadCollection(formId)

  useEffect(() => {
    setStep(step)
  }, [step, setStep])

  useEffect(() => {
    setFormId(formId)
  }, [formId, setFormId])
}

export function useFormOptions() {
  const { collections: { items } = {} } = useFormStore()

  return useCallback(async () => ({
    options: items.map(({ id, name }) => ({
      value: id,
      label: `[#${id}] ${name}`,
    })),
  }), [items])
}

export function useStepOptions() {
  const { collection: { item } } = useFormStore()

  return useCallback(async () => ({
    options: item?.definition?.map((_, idx) => ({
      value: `${idx + 1}`,
      label: `Step ${idx + 1}`,
    })),
  }), [item])
}

export function useFormProperties() {
  const formId = useCraftProp('formId')

  const { collections: { useLoad: useLoadCollections }, collection: { useLoad: useLoadCollection } } = useFormStore()
  useLoadCollections({ autoRefresh: false })
  useLoadCollection(formId.value)
}

export function useFormContainer() {
  const { form, step } = useContext()

  useFormContainerLoader()

  return {
    form: form.value,
    step: step.value,
  }
}

export function useFormContainerLoader() {
  const { id: containerNodeId } = useNode()
  const { setNodeId, form } = useContext()
  const { collection: { item }, loading } = useFormStore()

  useEffect(() => {
    setNodeId?.(containerNodeId)
  }, [containerNodeId, setNodeId])

  useEffect(() => {
    if (item) {
      form.setValue(item)
    }
  }, [item, form])

  useEffect(() => {
    if (loading) {
      form.setValue(null)
    }
  }, [form, loading])
}

export { useContext as useFormContext }
